// @flow

import { format } from 'date-fns'

import { limitText, joinNonEmpty } from './fields'

export const CRASH_CODE = 'APP-CRASH'

// Byte limits shared by the screen and the QR payload
export const FIELD_LIMITS = {
  errorType: 24,
  eventId: 32,
  sendStatus: 8,
  tag: 32,
  release: 32,
  platform: 32,
  device: 48,
  customerId: 32,
}

// Data shared by the support lines and the QR payload
export type CrashInfo = {
  eventId: string,
  sendStatus: string,
  version: string,
  revision: string,
  tag: string,
  os: string,
  osVersion: string,
  brand: string,
  model: string,
  customerId: string,
  time: number,
}

const joinLimited = (parts: Array<string>, separator: string, maxBytes: number): string => {
  const text = joinNonEmpty(
    parts.map((part) => limitText(part, maxBytes)),
    separator,
  )

  return limitText(text, maxBytes)
}

// Same format as the Sentry release: `${VERSION}-${GIT_REVISION}`
export const formatRelease = (info: CrashInfo): string =>
  joinLimited([info.version, info.revision], '-', FIELD_LIMITS.release)

export const formatPlatform = (info: CrashInfo): string =>
  joinLimited([info.os, info.osVersion], ' ', FIELD_LIMITS.platform)

export const formatDevice = (info: CrashInfo): string =>
  joinLimited([info.brand, info.model], ' ', FIELD_LIMITS.device)

// "Sentry 200 · ID …", or "Sentry ID …" while sending is off: the ID is the Sentry event ID
const formatSentryLine = (info: CrashInfo): string => {
  const eventId = limitText(info.eventId, FIELD_LIMITS.eventId)
  const sendStatus = limitText(info.sendStatus, FIELD_LIMITS.sendStatus)

  if (!eventId) return ''

  return sendStatus ? `Sentry ${sendStatus} · ID ${eventId}` : `Sentry ID ${eventId}`
}

// eslint-disable-next-line complexity
export const buildServiceLines = (info: CrashInfo & { errorType: string }): Array<string> => {
  const release = formatRelease(info)
  const tag = limitText(info.tag, FIELD_LIMITS.tag)
  const customerId = limitText(info.customerId, FIELD_LIMITS.customerId)

  return [
    joinNonEmpty([CRASH_CODE, limitText(info.errorType, FIELD_LIMITS.errorType)], ' · '),
    formatSentryLine(info),
    joinNonEmpty([release ? `v${release}` : '', tag ? `build ${tag}` : ''], ' · '),
    joinNonEmpty([formatPlatform(info), formatDevice(info)], ' · '),
    customerId ? `User ${customerId}` : '',
    format(info.time, 'DD.MM.YYYY HH:mm'),
  ].filter((line) => line.length > 0)
}
